// /admin → Final exams: the exam feed the app reads (GET /api/final-exams).
//
// Its own file for the reason ChangelogAdmin.js is: this tab PUBLISHES
// something rather than displaying it. Three doors onto one feed —
//
//  · Fetch from registrar now — the daily cron's sync, on demand.
//  · Paste exam rows — the fallback for the day the registrar's page changes
//    shape, or for a correction that cannot wait for noon UTC.
//  · Clear feed — the one way to an empty feed; the sync never clears.
//
// The status block at the top is the whole reason the tab exists on a quiet
// day: the cron runs unattended, and "did it work this morning" has to be
// answerable here rather than in Vercel's logs. See docs/FINAL_EXAMS.md.

import { useCallback, useEffect, useState } from 'react';

const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';

const PREVIEW_ROWS = 10;

const fmtWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' });
};

const fmtTime = (hhmm) => {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm || '');
  if (!m) return hhmm || '';
  const h = Number(m[1]);
  return `${((h + 11) % 12) + 1}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
};

export default function FinalExamsAdmin({ token }) {
  const [feed, setFeed] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [warnings, setWarnings] = useState([]);
  const [text, setText] = useState('');
  const [term, setTerm] = useState('');
  const [termName, setTermName] = useState('');

  const headers = useCallback(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API_BASE}/api/final-exams?meta=1`, { headers: headers() });
      const j = await r.json().catch(() => ({}));
      if (r.ok && j.ok) {
        setFeed(j.feed || null);
        setMeta(j.meta || null);
      } else setErr(j.error || `Could not load the feed (${r.status}).`);
    } catch (e) {
      setErr(`Could not load the feed: ${e.message}`);
    } finally {
      setLoaded(true);
    }
  }, [token, headers]);

  useEffect(() => { load(); }, [load]);

  const post = useCallback(async (body, label) => {
    setBusy(label);
    setErr('');
    setNotice('');
    setWarnings([]);
    try {
      const r = await fetch(`${API_BASE}/api/final-exams`, { method: 'POST', headers: headers(), body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (Array.isArray(j.warnings) && j.warnings.length) setWarnings(j.warnings);
      if (!r.ok) {
        setErr(j.error || `That did not work (${r.status}).`);
        return null;
      }
      await load();
      return j;
    } catch (e) {
      setErr(`That did not work: ${e.message}`);
      return null;
    } finally {
      setBusy('');
    }
  }, [headers, load]);

  const fetchNow = async () => {
    const j = await post({ op: 'fetch' }, 'fetch');
    if (!j) return;
    if (j.stored) setNotice(`Fetched ${j.count} exams for ${j.termName || j.term} from the registrar.`);
    else setErr(j.error || j.reason || 'The registrar page was not stored.');
  };

  const publishPaste = async () => {
    if (!text.trim()) { setErr('Paste some rows first.'); return; }
    const j = await post({ op: 'paste', text, term: term.trim() || undefined, termName: termName.trim() || undefined }, 'paste');
    if (!j) return;
    setNotice(`Published ${j.count} pasted exams as ${j.termName || j.term}.`);
    setText('');
  };

  const clear = async () => {
    if (!window.confirm('Clear the exam feed? Every phone will see no exam timetable until something is published again.')) return;
    const j = await post({ op: 'clear' }, 'clear');
    if (j) setNotice('Feed cleared.');
  };

  const preview = (feed?.exams || []).slice(0, PREVIEW_ROWS);

  return (
    <div>
      <div style={S.top}>
        <div>
          <div style={S.topTitle}>/api/final-exams</div>
          <div style={S.topNote}>
            The final exam timetable the app matches against a student's courses. Synced daily from the{' '}
            <a style={S.link} href="https://umanitoba.ca/registrar/final-exams/final-exam-schedule" target="_blank" rel="noreferrer">
              registrar's page
            </a>
            ; paste rows below when that page changes shape or a correction cannot wait.
          </div>
        </div>
        <div style={S.actions}>
          <button style={{ ...S.btnPrimary, ...(busy ? S.btnOff : {}) }} disabled={!!busy} onClick={fetchNow}>
            {busy === 'fetch' ? 'Fetching…' : 'Fetch from registrar now'}
          </button>
          <button style={{ ...S.btnDanger, ...(busy || !feed ? S.btnOff : {}) }} disabled={!!busy || !feed} onClick={clear}>
            {busy === 'clear' ? 'Clearing…' : 'Clear feed'}
          </button>
        </div>
      </div>

      {!!err && <div style={S.err}>{err}</div>}
      {!!notice && <div style={S.notice}>{notice}</div>}
      {warnings.length > 0 && (
        <div style={S.warn}>
          <div style={S.warnTitle}>{warnings.length} warning{warnings.length === 1 ? '' : 's'} from the parse</div>
          <ul style={S.warnList}>
            {warnings.slice(0, 40).map((w, i) => <li key={i}>{w}</li>)}
            {warnings.length > 40 && <li>…and {warnings.length - 40} more</li>}
          </ul>
        </div>
      )}

      {/* ── Status ─────────────────────────────────────────────────────── */}
      <div style={S.card}>
        <div style={S.cardTitle}>Published feed</div>
        {!loaded && <div style={S.emptyNote}>Loading…</div>}
        {loaded && !feed && <div style={S.emptyNote}>Nothing published. The app shows no exam timetable.</div>}
        {loaded && feed && (
          <div style={S.facts}>
            <Fact label="Term">{feed.termName} <span style={S.mono}>{feed.term}</span></Fact>
            <Fact label="Exams">{feed.count}</Fact>
            <Fact label="Source">
              {feed.source === 'registrar' ? 'registrar page' : 'pasted by the operator'}
              {feed.fetchedAt ? ` · fetched ${fmtWhen(feed.fetchedAt)}` : ''}
            </Fact>
            <Fact label="Published">{fmtWhen(feed.updatedAt)}</Fact>
          </div>
        )}
        {loaded && (
          <div style={S.facts}>
            <Fact label="Last sync">
              {!meta?.lastSyncAt && 'never'}
              {!!meta?.lastSyncAt && (
                <>
                  <span style={{ ...S.state, ...(meta.lastSyncOk ? S.stateOk : S.stateBad) }}>
                    {meta.lastSyncOk ? 'ok' : 'failed'}
                  </span>{' '}
                  {fmtWhen(meta.lastSyncAt)}
                  {Number.isFinite(meta.lastSyncCount) ? ` · ${meta.lastSyncCount} rows on the page` : ''}
                  {meta.lastSyncError ? <div style={S.syncErr}>{meta.lastSyncError}</div> : null}
                </>
              )}
            </Fact>
            <Fact label="Cron">daily at 12:00 UTC (6–7 am Winnipeg)</Fact>
          </div>
        )}

        {preview.length > 0 && (
          <>
            <div style={S.previewTitle}>First {preview.length} of {feed.count}</div>
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>
                    {['Course', 'Section', 'Date', 'Time', 'Hours', 'Location', 'Alpha splits', 'Seats'].map((h) => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.map((e, i) => (
                    <tr key={`${e.course}-${e.section}-${i}`}>
                      <td style={S.td}>{e.course}</td>
                      <td style={S.td}>{e.section}</td>
                      <td style={S.td}>{e.date}</td>
                      <td style={S.td}>{fmtTime(e.time)}</td>
                      <td style={S.td}>{e.hours ?? '—'}</td>
                      <td style={S.td}>{e.location || '—'}</td>
                      <td style={S.td}>{e.splits || '—'}</td>
                      <td style={S.td}>{e.seats || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* ── Paste ──────────────────────────────────────────────────────── */}
      <div style={S.card}>
        <div style={S.cardTitle}>Paste exam rows</div>
        <div style={S.hint}>
          One exam per line, tab-, pipe- or comma-separated, with or without a header line. Either the registrar's
          columns — <span style={S.mono}>Course, Section, Date, Time, Location, Alpha splits, Seats, Hours</span> —, the
          published six — <span style={S.mono}>Course, Section, Date, Time, Location, Hours</span> (e.g.{' '}
          <span style={S.mono}>ACC 1100 A01 Dec 20 1:30 PM RO 3</span>) — or the short five: <span style={S.mono}>Course, Section, Date, Start time, Duration</span> (a duration of{' '}
          <span style={S.mono}>2:30</span> is two and a half hours). Dates like <span style={S.mono}>Dec 13</span>,{' '}
          <span style={S.mono}>Dec 13, 2026</span>, <span style={S.mono}>12/13/2026</span> or{' '}
          <span style={S.mono}>2026-12-13</span>. Publishing REPLACES the whole feed.
        </div>
        <textarea
          style={S.textarea}
          rows={10}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={'ACC 1110\tA01\tDec 13\t6:00 PM\t122 Drake Centre\t\t1 - 34\t3'}
          spellCheck={false}
        />
        <div style={S.row}>
          <div style={S.fieldNarrow}>
            <label style={S.label}>Term code <span style={S.opt}>optional</span></label>
            <input style={S.input} value={term} onChange={(e) => setTerm(e.target.value)} placeholder="202690" />
          </div>
          <div style={S.fieldGrow}>
            <label style={S.label}>Term name <span style={S.opt}>optional</span></label>
            <input style={S.input} value={termName} onChange={(e) => setTermName(e.target.value)} placeholder="Fall 2026" />
          </div>
        </div>
        <div style={S.hint}>Left blank, the term is worked out from the dates (December → Fall, April → Winter, June–August → Summer).</div>
        <div style={S.actions}>
          <button style={{ ...S.btnPrimary, ...(busy || !text.trim() ? S.btnOff : {}) }} disabled={!!busy || !text.trim()} onClick={publishPaste}>
            {busy === 'paste' ? 'Publishing…' : 'Publish pasted rows'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Fact({ label, children }) {
  return (
    <div style={S.fact}>
      <div style={S.factLabel}>{label}</div>
      <div style={S.factValue}>{children}</div>
    </div>
  );
}

const S = {
  top: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' },
  topTitle: { fontSize: 14, fontWeight: 800, color: '#202124' },
  topNote: { fontSize: 12.5, color: '#5f6368', marginTop: 2, maxWidth: 640, lineHeight: 1.45 },
  link: { color: '#1a73e8', fontWeight: 700, textDecoration: 'none' },
  err: { background: '#fce8e6', color: '#c5221f', borderRadius: 8, padding: '8px 12px', fontSize: 13, fontWeight: 600, marginBottom: 10 },
  notice: { background: '#e6f4ea', color: '#137333', borderRadius: 8, padding: '8px 12px', fontSize: 13, fontWeight: 600, marginBottom: 10 },
  warn: { background: '#fef7e0', color: '#7a5a00', borderRadius: 8, padding: '8px 12px', fontSize: 12.5, marginBottom: 10 },
  warnTitle: { fontWeight: 800, marginBottom: 4 },
  warnList: { margin: 0, paddingLeft: 18, lineHeight: 1.5 },

  card: { background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: 16, marginBottom: 14 },
  cardTitle: { fontSize: 13, fontWeight: 800, color: '#202124', marginBottom: 10 },
  facts: { display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 10 },
  fact: { minWidth: 140 },
  factLabel: { fontSize: 11, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: '#80868b', marginBottom: 2 },
  factValue: { fontSize: 13.5, color: '#202124', lineHeight: 1.45 },
  mono: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, color: '#5f6368' },
  state: { fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' },
  stateOk: { background: '#e6f4ea', color: '#137333' },
  stateBad: { background: '#fce8e6', color: '#c5221f' },
  syncErr: { fontSize: 12.5, color: '#c5221f', marginTop: 2 },
  emptyNote: { fontSize: 13, color: '#80868b', lineHeight: 1.45, margin: '0 0 8px' },

  previewTitle: { fontSize: 11.5, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: '#5f6368', margin: '8px 0 6px' },
  tableWrap: { overflowX: 'auto' },
  table: { borderCollapse: 'collapse', width: '100%', fontSize: 12.5 },
  th: { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #dadce0', color: '#5f6368', fontWeight: 700, whiteSpace: 'nowrap' },
  td: { padding: '6px 8px', borderBottom: '1px solid #f1f3f4', color: '#202124', whiteSpace: 'nowrap' },

  hint: { fontSize: 12.5, color: '#5f6368', lineHeight: 1.5, marginBottom: 8 },
  textarea: { width: '100%', boxSizing: 'border-box', border: '1px solid #dadce0', borderRadius: 8, padding: '8px 10px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12.5, lineHeight: 1.5, resize: 'vertical', marginBottom: 10 },
  row: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  fieldGrow: { flex: 1, minWidth: 200, marginBottom: 8 },
  fieldNarrow: { width: 140, marginBottom: 8 },
  label: { display: 'block', fontSize: 11.5, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: '#5f6368', marginBottom: 5 },
  opt: { fontWeight: 600, letterSpacing: 0, textTransform: 'none', color: '#9aa0a6' },
  input: { width: '100%', boxSizing: 'border-box', border: '1px solid #dadce0', borderRadius: 8, padding: '8px 10px', font: 'inherit', fontSize: 14 },

  actions: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 4 },
  btnPrimary: { background: '#1a73e8', color: '#fff', border: '1.5px solid #1a73e8', borderRadius: 8, padding: '8px 14px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  btnDanger: { background: '#fff', color: '#c5221f', border: '1.5px solid #dadce0', borderRadius: 8, padding: '8px 14px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  btnOff: { background: '#f1f3f4', color: '#9aa0a6', border: '1.5px solid #f1f3f4', cursor: 'default' },
};
